import { useState } from 'react';
import { Globe, Link2, Copy, Check, ExternalLink, Eye, EyeOff, Power } from 'lucide-react';
import type { QueryFormData, SolaceConfig, SapBtpConfig } from '../../../types/queryForm';
import type { AvailabilityType, AutomationInterval } from '../../../types/query';
import { AUTOMATION_INTERVALS } from '../../../types/query';
import { queryService } from '../../../services/queryService';

interface Step3AvailabilityProps {
  formData: QueryFormData;
  onChange: (data: Partial<QueryFormData>) => void;
  availableColumns: string[];
}

export default function Step3Availability({
  formData,
  onChange,
  availableColumns
}: Step3AvailabilityProps) {
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [showSolacePassword, setShowSolacePassword] = useState(false);
  const [showSapSecret, setShowSapSecret] = useState(false);

  const endpointUrl = queryService.generateEndpointUrl(formData.endpointSlug);

  const handleSlugChange = (value: string) => {
    const cleanedSlug = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    onChange({ endpointSlug: cleanedSlug });

    if (cleanedSlug && !queryService.validateSlug(cleanedSlug)) {
      setSlugError('Slug deve conter apenas letras minusculas, numeros e hifens');
    } else {
      setSlugError('');
    }
  };

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(endpointUrl);
      setCopiedEndpoint(true);
      setTimeout(() => setCopiedEndpoint(false), 2000);
    } catch (error) {
      console.error('Failed to copy endpoint:', error);
    }
  };

  const handleAvailabilityChange = (type: AvailabilityType) => {
    onChange({ availabilityType: type });
  };

  const handleSolaceChange = (config: Partial<SolaceConfig>) => {
    onChange({
      solaceConfig: { ...formData.solaceConfig, ...config }
    });
  };

  const handleSapBtpChange = (config: Partial<SapBtpConfig>) => {
    onChange({
      sapBtpConfig: { ...formData.sapBtpConfig, ...config }
    });
  };

  const columnsToShow = formData.selectedColumns.length > 0
    ? formData.selectedColumns
    : availableColumns;

  const isApiEnabled = formData.availabilityType === 'api' || formData.availabilityType === 'both';
  const isIntegrationEnabled = formData.availabilityType === 'integration' || formData.availabilityType === 'both';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Disponibilizacao</h3>
        <p className="text-sm text-gray-500">Escolha como este serviço sera disponibilizado.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          type="button"
          onClick={() => handleAvailabilityChange('api')}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            formData.availabilityType === 'api'
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Globe className={`w-6 h-6 mb-2 ${formData.availabilityType === 'api' ? 'text-blue-600' : 'text-gray-400'}`} />
          <div className="font-medium text-gray-900">Somente API</div>
          <p className="text-xs text-gray-500 mt-1">Disponibilizar via endpoint REST</p>
        </button>

        <button
          type="button"
          onClick={() => handleAvailabilityChange('integration')}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            formData.availabilityType === 'integration'
              ? 'border-orange-600 bg-orange-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Link2 className={`w-6 h-6 mb-2 ${formData.availabilityType === 'integration' ? 'text-orange-600' : 'text-gray-400'}`} />
          <div className="font-medium text-gray-900">Somente Integracao</div>
          <p className="text-xs text-gray-500 mt-1">Enviar dados para plataformas externas</p>
        </button>

        <button
          type="button"
          onClick={() => handleAvailabilityChange('both')}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            formData.availabilityType === 'both'
              ? 'border-green-600 bg-green-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex gap-1 mb-2">
            <Globe className={`w-5 h-5 ${formData.availabilityType === 'both' ? 'text-green-600' : 'text-gray-400'}`} />
            <Link2 className={`w-5 h-5 ${formData.availabilityType === 'both' ? 'text-green-600' : 'text-gray-400'}`} />
          </div>
          <div className="font-medium text-gray-900">Ambos</div>
          <p className="text-xs text-gray-500 mt-1">API e integracao simultaneamente</p>
        </button>
      </div>

      {isApiEnabled && (
        <div className="border border-blue-200 rounded-lg p-4 space-y-4 bg-blue-50/30">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">Configuracao da API</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endpoint Slug *
              </label>
              <input
                type="text"
                value={formData.endpointSlug}
                onChange={(e) => handleSlugChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  slugError ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: listar-usuarios-ativos"
              />
              {slugError && (
                <p className="text-xs text-red-600 mt-1">{slugError}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => onChange({ isActive: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="flex items-center gap-2 text-sm text-gray-700">
                <Power className="w-4 h-4" />
                Endpoint ativo
              </label>
            </div>
          </div>

          {formData.endpointSlug && (
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">URL:</span>
              <code className="flex-1 text-sm text-blue-700 truncate">{endpointUrl}</code>
              <button
                type="button"
                onClick={handleCopyEndpoint}
                className={`p-1.5 rounded transition-colors ${
                  copiedEndpoint ? 'text-green-600' : 'text-gray-400 hover:text-blue-600'
                }`}
              >
                {copiedEndpoint ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <a
                href={endpointUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}

          {formData.filterColumns.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Filtros Disponiveis:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.filterColumns.map((col) => (
                  <span key={col} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    ?{col}=valor
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isIntegrationEnabled && (
        <div className="border border-orange-200 rounded-lg p-4 space-y-6 bg-orange-50/30">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-semibold text-orange-900">Configuracao de Integracoes</span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="solaceEnabled"
                checked={formData.solaceConfig.enabled}
                onChange={(e) => handleSolaceChange({ enabled: e.target.checked })}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="solaceEnabled" className="text-sm font-medium text-gray-900">
                Solace Event Broker
              </label>
            </div>

            {formData.solaceConfig.enabled && (
              <div className="pl-7 space-y-4 border-l-2 border-orange-200 ml-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Broker URL *</label>
                    <input
                      type="url"
                      value={formData.solaceConfig.brokerUrl}
                      onChange={(e) => handleSolaceChange({ brokerUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="tcps://broker.solace.cloud:55443"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VPN Name *</label>
                    <input
                      type="text"
                      value={formData.solaceConfig.vpnName}
                      onChange={(e) => handleSolaceChange({ vpnName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="default"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Queue/Topic Name *</label>
                  <input
                    type="text"
                    value={formData.solaceConfig.queueName}
                    onChange={(e) => handleSolaceChange({ queueName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="query/results/topic"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                    <input
                      type="text"
                      value={formData.solaceConfig.username}
                      onChange={(e) => handleSolaceChange({ username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      placeholder="solace-client"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <div className="relative">
                      <input
                        type={showSolacePassword ? 'text' : 'password'}
                        value={formData.solaceConfig.password}
                        onChange={(e) => handleSolaceChange({ password: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSolacePassword(!showSolacePassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                      >
                        {showSolacePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequencia de Envio
                  </label>
                  <select
                    value={formData.solaceConfig.interval}
                    onChange={(e) => handleSolaceChange({ interval: e.target.value as AutomationInterval })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white text-sm"
                  >
                    {AUTOMATION_INTERVALS.map((interval) => (
                      <option key={interval.value} value={interval.value}>
                        {interval.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Os dados serao enviados automaticamente para o Solace nesta periodicidade
                  </p>
                </div>

                {columnsToShow.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campos enviados:</span>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {columnsToShow.map((col) => (
                        <span key={col} className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-orange-200 pt-4 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="sapBtpEnabled"
                checked={formData.sapBtpConfig.enabled}
                onChange={(e) => handleSapBtpChange({ enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="sapBtpEnabled" className="text-sm font-medium text-gray-900">
                SAP Business Technology Platform
              </label>
            </div>

            {formData.sapBtpConfig.enabled && (
              <div className="pl-7 space-y-4 border-l-2 border-blue-200 ml-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service URL *</label>
                    <input
                      type="url"
                      value={formData.sapBtpConfig.serviceUrl}
                      onChange={(e) => handleSapBtpChange({ serviceUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="https://api.sap.com/service"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Path *</label>
                    <input
                      type="text"
                      value={formData.sapBtpConfig.apiPath}
                      onChange={(e) => handleSapBtpChange({ apiPath: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="/api/v1/data"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Token URL (OAuth) *</label>
                  <input
                    type="url"
                    value={formData.sapBtpConfig.tokenUrl}
                    onChange={(e) => handleSapBtpChange({ tokenUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="https://auth.sap.com/oauth/token"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client ID *</label>
                    <input
                      type="text"
                      value={formData.sapBtpConfig.clientId}
                      onChange={(e) => handleSapBtpChange({ clientId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="sb-client-id"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret *</label>
                    <div className="relative">
                      <input
                        type={showSapSecret ? 'text' : 'password'}
                        value={formData.sapBtpConfig.clientSecret}
                        onChange={(e) => handleSapBtpChange({ clientSecret: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSapSecret(!showSapSecret)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                      >
                        {showSapSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequencia de Envio
                  </label>
                  <select
                    value={formData.sapBtpConfig.interval}
                    onChange={(e) => handleSapBtpChange({ interval: e.target.value as AutomationInterval })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                  >
                    {AUTOMATION_INTERVALS.map((interval) => (
                      <option key={interval.value} value={interval.value}>
                        {interval.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Os dados serao enviados automaticamente para o SAP BTP nesta periodicidade
                  </p>
                </div>

                {columnsToShow.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campos enviados:</span>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {columnsToShow.map((col) => (
                        <span key={col} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          {col}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
